import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { useNotify } from '../../../components/notifications';
import { BusyIcon } from '../../../components/loading';
import { DateFilterSelect, monthsOf, type DateFilterId, DEFAULT_DATE_FILTER } from './IcmDateFilter';
import { ICM_LIST_TAKE } from '../icmPaging';
import { useConfirm, useSaveFlow } from '../../../components/confirm';
import { downloadLabelReport } from '../tracking/icmTrackingApi';
import { fmtLocalDate } from '../../../utils/datetime';
import { useAuthStore } from '../../../auth/store';
import '../../backlog/backlog.css';
import '../../modules/SettingsDetailPage.css';
import './IcmDetailGrid.css';

/** Item Distribution MASTER row — one RECEIVE BATCH (RC#) still pending
 *  distribution. Legacy Distribution_ListView_MainDistribute groups the
 *  Status=0 rows by ReceiveID and shows one row per batch
 *  (DistributionViewController.cs ~225-232); the drill-down then filters
 *  [ReceiveID] = ? And [Status] = 0 (~1411). */
interface DistributionBatchRow {
  receiveId: string;
  vendorName: string | null;
  poNumber: string | null;
  totalItems: number;
  receiveDate: string | null;
}

/** Item Distribution DETAIL line — the legacy Distribution_ListView columns. */
interface DistributionLineRow {
  oid: string;
  itemName: string | null;
  description: string | null;
  catalog: string | null;
  orderQty: number;
  itemUnit: number;
  totalItems: number;
  itemReceived: string | null;
  vendorLt: string | null;
  /** The Item's IsVendorLT flag — when set, a Vendor Lot number is MANDATORY to
   *  distribute this line (the SP throws 50024). Drives the required tint and
   *  the pre-save check, so the rule is visible before Save rather than after. */
  isVendorLt: boolean;
  expiryDate: string | null;
  /** The FK values the row already holds. Seeded into the draft so an untouched
   *  row re-posts what it had — the page used to resolve Storage / GivenTo by
   *  matching the display NAME back to the option list, which silently produced
   *  null for a row whose stored value wasn't in the list. */
  storageId: string | null;
  storageName: string | null;
  givenToId: string | null;
  givenToName: string | null;
  lt: string | null;
  distributionDate: string | null;
  distributedByName: string | null;
}

/** Item Distribution HISTORY row — one distributed LOT (legacy
 *  Distribution_ListView_Viewmode). The column set below is the merge of the
 *  two legacy model layers with the deployed GoLive model winning; see
 *  AI_ICM_Distribution_History_SP for the derivation. */
interface DistributionHistoryRow {
  oid: string;
  receiveId: string | null;
  itemName: string | null;
  description: string | null;
  catalog: string | null;
  orderQty: number;
  itemUnit: number;
  totalItems: number;
  itemReceived: string | null;
  vendorLt: string | null;
  expiryDate: string | null;
  storageName: string | null;
  givenToName: string | null;
  lt: string | null;
  distributionDate: string | null;
  distributedByName: string | null;
  /** Raw LTStatus — Rollback keys on the VALUE (legacy blocks Consumed = 2 and
   *  Disposed = 4); the grid shows `statusLabel`. */
  status: number;
  statusLabel: string;
  totalCount: number;
}

/** Legacy's own wording, so the AI form says exactly what ALPACA says
 *  (BTLIMS.Web/Model.xafml LocalizationItems). */
const MSG = {
  selectRow:      'Please select an item to proceed further.',   // selectchkbox
  rollbackFail:   'Item cannot be rollback.',                    // rollbackfail
  rollbackReason: 'Please enter the rollback reason.',           // rollbackreason
  rollbackOk:     'Rollbacked successfully.',                    // Rollbacksuccess
  noLt:           'Cannot generate report for pending distribution records.', // pendingdistribute
  // Distribute validation — emptyvendorlt / selectgivento / expirydate. The
  // resource for `selectgivento` carries a trailing space; only that is dropped.
  emptyVendorLt:  'VendorLT should not be empty.',               // emptyvendorlt
  selectGivenTo:  'Select givento or storage.',                  // selectgivento
  expiryDate:     'ExpiryDate should not be empty.',             // expirydate
} as const;

/** LTStatus values legacy refuses to roll back (DistributeRollback_Execute
 *  ~2686): Consumed and Disposed. Everything else it reverses. */
const ROLLBACK_BLOCKED = new Set([2, 4]);

/** One line's pending batch edit. Holds the resolved FK **and** its display
 *  name: the combo only ever yields a name, so the id is resolved once at
 *  commit rather than re-derived at Save. */
interface LineEdit {
  storageId: string | null;
  storageName: string;
  givenToId: string | null;
  givenToName: string;
  vendorLt: string;
  expiryDate: string;
}

const BLANK_EDIT: LineEdit = {
  storageId: null, storageName: '', givenToId: null, givenToName: '', vendorLt: '', expiryDate: '',
};

export default function DistributionListPage() {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const { confirm, prompt } = useConfirm();

  // master
  const [batches, setBatches] = useState<DistributionBatchRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [lineSearch, setLineSearch] = useState('');
  const [histSearch, setHistSearch] = useState('');
  /** Legacy's History button (Distributionview_Execute ~3111) does NOT re-run
   *  the pending grid unfiltered -- it opens Distribution_ListView_Viewmode, a
   *  DIFFERENT view over a FLAT Distribution collection filtered
   *  `[Status] <> 'PendingDistribute'`: one row per distributed LOT, not one per
   *  receive batch. AI had re-queried the RC#-grouped batch list with the status
   *  filter merely dropped, which is why History showed only RC# / Vendor / PO#
   *  / TotalItems / ReceiveDate and none of the lot columns (owner report
   *  2026-09-26). It is now its own view with its own rows, columns and bar. */
  const [history, setHistory] = useState(false);
  const [histRows, setHistRows] = useState<DistributionHistoryRow[]>([]);
  const [histTotal, setHistTotal] = useState(0);
  const [histSel, setHistSel] = useState<Set<string | number>>(new Set());
  /* No Date Filter control on the MAIN bar any more, so this can no longer
     change — kept as a constant rather than dead state, and the query stays
     unfiltered. The HISTORY view keeps its own picker (histDateFilter). */
  const dateFilter: DateFilterId = 'ALL';
  /** History opens on its OWN window. Legacy takes the default from
   *  DefaultSetting.InventoryWorkFlow, which is OneMonth on the live database --
   *  and with 5.8k historic lots an unbounded first load is not a default worth
   *  porting differently. */
  const [histDateFilter, setHistDateFilter] = useState<DateFilterId>(DEFAULT_DATE_FILTER);
  /** Legacy's Scan box on this form -- an EXACT LT lookup, as on Consumption. */
  const [scan, setScan] = useState('');
  /** Per-row batch edits, keyed by row Oid — legacy's InlineEditMode="Batch".
   *  Storage / GivenTo / VendorLT / ExpiryDate are the columns legacy leaves
   *  editable on Distribution_ListView; AI rendered all of them read-only, so
   *  the distribute rules ("Storage location is required.", "Given To employee
   *  is required.", "Vendor Lot number is required for this item.") could never
   *  be satisfied and Save always failed. */
  const [edits, setEdits] = useState<Record<string, LineEdit>>({});
  const [storageOpts, setStorageOpts] = useState<{ id: string; name: string }[]>([]);
  const [employeeOpts, setEmployeeOpts] = useState<{ id: string; name: string }[]>([]);

  // detail
  const [openBatch, setOpenBatch] = useState<DistributionBatchRow | null>(null);
  const [lines, setLines] = useState<DistributionLineRow[]>([]);
  const [linesLoading, setLinesLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  /* ── GoLive's tick preview (LT# · DistributionDate · DistributedBy) ────────
     Ticking a row in GoLive fills those three immediately and unticking clears
     them; only Save commits. Held in a map keyed by row id rather than written
     into `lines`, so the server's own data is never mutated and an untick is a
     plain delete. The LT comes from the read-only preview endpoint — the number
     is ALLOCATED race-safely by the distribute SP on Save, not here, because
     legacy's allocate-on-tick can hand two users the same LT (§14a Rule B). */
  const [ltPreview, setLtPreview] = useState<Record<string, string>>({});
  const currentUserLabel =
    useAuthStore(s => s.user?.userName || s.user?.name) ?? '';

  /** Today, shown the instant a row is ticked — legacy sets `new Date()`. */
  const tickDate = useMemo(() => new Date().toISOString(), []);

  /* Truthiness, NOT `??`, on every one of these: the reader returns an EMPTY
     STRING for an undistributed row's DistributedBy rather than null, and
     `'' ?? x` keeps the empty string — which is exactly why DistributedBy stayed
     blank on tick while LT# and the date (already written as truthiness checks)
     filled correctly. */
  const isTicked     = (r: DistributionLineRow) => selected.has(r.oid);
  const ltPreviewFor = (r: DistributionLineRow) => (isTicked(r) ? ltPreview[r.oid] ?? '' : '');
  const distDateFor  = (r: DistributionLineRow) =>
    r.distributionDate ? fmtLocalDate(r.distributionDate) ?? ''
                       : isTicked(r) ? fmtLocalDate(tickDate) ?? '' : '';
  const distByFor    = (r: DistributionLineRow) =>
    r.distributedByName ? r.distributedByName
                        : isTicked(r) ? currentUserLabel : '';

  /* Ask the server what the ticked rows would be numbered. Runs on every
     selection change (tick, untick, select-all) — the same points at which
     legacy raises its `distributeHandler` callback. Clearing the selection
     clears the preview without a round trip. */
  useEffect(() => {
    const ids = Array.from(selected).map(String);
    if (!openBatch || ids.length === 0) { setLtPreview({}); return; }
    let cancelled = false;
    (async () => {
      try {
        // `as never` is how every other caller passes silentLoading — it lives on
        // the client's internal config type, not on AxiosRequestConfig. Silent so
        // ticking a row never flashes the global loader.
        const res = await api.post<{ oid: string; previewLt: string | null }[]>(
          '/api/v1/distributions/preview-lt', { ids }, { silentLoading: true } as never);
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const p of (res.data ?? []) as { oid: string; previewLt: string | null }[]) {
          if (p.previewLt) map[p.oid] = p.previewLt;
        }
        setLtPreview(map);
      } catch {
        // A preview is cosmetic — if it fails the row still saves and the SP
        // mints the real number, so this must never block or shout.
        if (!cancelled) setLtPreview({});
      }
    })();
    return () => { cancelled = true; };
  }, [selected, openBatch]);

  /** Row-selection ticks on the RECEIVE-BATCH master (owner request 2026-09-25).
   *  Legacy sets ShowSelectionColumn="False" on Distribution_ListView_MainDistribute,
   *  so this column is an AI addition rather than a port. Separate from
   *  `selected`, which is the DETAIL grid's own selection and drives
   *  Distribute / Rollback. */
  const [batchSel, setBatchSel] = useState<Set<string | number>>(new Set());
  const [acting, setActing] = useState(false);

  const loadBatches = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const res = await api.get('/api/v1/distributions/batches', {
        params: { take: ICM_LIST_TAKE, months: monthsOf(dateFilter) },
      });
      setBatches((res.data as { items?: DistributionBatchRow[] }).items ?? []);
    } catch {
      setError('Failed to load receive batches.');
      setBatches([]);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [dateFilter]);

  /** Item Distribution HISTORY — the flat distributed-lot list. */
  const loadHistory = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const res = await api.get('/api/v1/distributions/history', {
        params: { months: monthsOf(histDateFilter), lt: scan.trim() || null, take: ICM_LIST_TAKE },
      });
      // PagedResult<T> is (Items, Total) — `items`, as the batch list reads it.
      const page = res.data as { items?: DistributionHistoryRow[]; total?: number };
      setHistRows(page.items ?? []);
      setHistTotal(page.total ?? 0);
      setHistSel(new Set());
    } catch {
      setError('Failed to load the distribution history.');
      setHistRows([]); setHistTotal(0);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [histDateFilter, scan]);

  const loadLines = useCallback(async (receiveId: string) => {
    setLinesLoading(true);
    try {
      const res = await api.get(`/api/v1/distributions/by-receive/${encodeURIComponent(receiveId)}`);
      const rows = (res.data as DistributionLineRow[]) ?? [];
      setLines(rows);
      // Seed the batch editor from the row as loaded — INCLUDING the FK ids, so
      // an untouched cell re-posts exactly what the record already held rather
      // than whatever a name lookup happens to match.
      const seed: Record<string, LineEdit> = {};
      for (const r of rows) {
        seed[r.oid] = {
          storageId:   r.storageId ?? null,
          storageName: r.storageName ?? '',
          givenToId:   r.givenToId ?? null,
          givenToName: r.givenToName ?? '',
          vendorLt:    r.vendorLt ?? '',
          expiryDate:  r.expiryDate ? r.expiryDate.slice(0, 10) : '',
        };
      }
      setEdits(seed);
    } catch {
      notify.error('Failed to load the lines for this receive batch.');
      setLines([]);
    } finally {
      setLinesLoading(false);
    }
  }, [notify]);

  /** Storage + GivenTo are lookups on the legacy grid, so the editor needs
   *  their option lists. Both endpoints already existed. */
  const loadLookups = useCallback(async () => {
    try {
      const [st, emp] = await Promise.all([
        api.get('/api/v1/distributions/storage-options'),
        api.get('/api/v1/distributions/employee-options'),
      ]);
      const arr = (d: unknown) => (Array.isArray(d) ? d : ((d as { data?: unknown[] })?.data ?? [])) as Array<Record<string, unknown>>;
      setStorageOpts(arr(st.data).map(o => ({
        id: String(o.oid ?? o.id ?? ''), name: String(o.storage ?? o.name ?? ''),
      })).filter(o => o.name));
      setEmployeeOpts(arr(emp.data).map(o => ({
        id: String(o.oid ?? o.id ?? ''), name: String(o.displayName ?? o.name ?? o.fullName ?? ''),
      })).filter(o => o.name));
    } catch { /* non-fatal: the combo just has no options */ }
  }, []);

  useEffect(() => { void loadLookups(); }, [loadLookups]);
  useEffect(() => {
    if (history) void loadHistory();
    else void loadBatches();
  }, [history, loadHistory, loadBatches]);

  /** The TOP-BAR title follows the view (owner request 2026-09-26: "when click
   *  the History button, then the form title should be Item Distribution
   *  History"). <FormTitle>'s own `title` is deprecated — BO UI Standard v3 puts
   *  the page title in the AppShell top bar — so this uses the shared
   *  `ai:topbar-title` override, exactly as the sibling Ordering Items History
   *  does (standing rule 8). */
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('ai:topbar-title',
      { detail: history && !openBatch ? 'Item Distribution History' : null }));
    return () => { window.dispatchEvent(new CustomEvent('ai:topbar-title', { detail: null })); };
  }, [history, openBatch]);

  function openReceiveBatch(b: DistributionBatchRow) {
    setOpenBatch(b);
    setSelected(new Set());
    void loadLines(b.receiveId);
  }

  function backToList() {
    setOpenBatch(null);
    setLines([]);
    setSelected(new Set());
  }

  /** Leave History for the pending batch list. Legacy re-navigates from the
   *  menu (its History action lives only on MainDistribute); the breadcrumb is
   *  AI's equivalent. */
  function backToPending() {
    setHistory(false);
    setHistSel(new Set());
    setScan('');
  }

  const filteredBatches = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return batches;
    return batches.filter(b =>
      (b.receiveId ?? '').toLowerCase().includes(t) ||
      (b.vendorName ?? '').toLowerCase().includes(t) ||
      (b.poNumber ?? '').toLowerCase().includes(t)
    );
  }, [batches, search]);

  /** Ticked rows export on their own; with nothing ticked the whole list does —
   *  the ordinary grid convention, so the new tick column is not decoration. */
  const batchesExport = batchSel.size
    ? filteredBatches.filter(b => batchSel.has(b.receiveId))
    : filteredBatches;

  // Master columns — legacy order: RC# · Vendor · PO# · TotalItems · ReceiveDate.
  /** Is one row missing what rule `n` requires?  The three rules are ALPACA
   *  GoLive's, exactly as DistributionViewController.Distribute_Execute states
   *  them (lines 1768-1782):
   *    1. a Vendor LT, when the ITEM carries IsVendorLT;
   *    2. `Storage == null && givento == null` — an AND, so EITHER one satisfies
   *       it. (AI used to demand BOTH, which is stricter than the lab's own
   *       system and produced two complaints per row for one legacy rule.)
   *    3. an ExpiryDate. (AI did not check this at all; legacy requires it.) */
  const breaksRule = useCallback((row: DistributionLineRow, rule: 1 | 2 | 3): boolean => {
    const e = edits[row.oid] ?? BLANK_EDIT;
    if (rule === 1) return row.isVendorLt && !e.vendorLt.trim();
    if (rule === 2) return !e.storageId && !e.givenToId;
    return !e.expiryDate;
  }, [edits]);

  const batchCols: AppColumn<DistributionBatchRow>[] = [
    { field: 'receiveId',   header: 'RC#',         width: 150, text: b => b.receiveId ?? '—', render: b => b.receiveId ?? '—' },
    { field: 'vendorName',  header: 'Vendor',      width: 200, text: b => b.vendorName ?? '—', render: b => b.vendorName ?? '—' },
    { field: 'poNumber',        header: 'PO#',         width: 150, text: b => b.poNumber ?? '—', render: b => b.poNumber ?? '—' },
    { field: 'totalItems',  header: 'TotalItems',  width: 100, type: 'number', text: b => String(b.totalItems), value: b => b.totalItems },
    { field: 'receiveDate', header: 'ReceiveDate', width: 120, text: b => b.receiveDate ? fmtLocalDate(b.receiveDate) : '', render: b => b.receiveDate ? fmtLocalDate(b.receiveDate) : '' },
  ];

  /** HISTORY columns — the legacy Distribution_ListView_Viewmode set, in the
   *  merged model order (base + the deployed GoLive override, live winning):
   *  RC# . Item . Description . Catalog# . OrderQty . Item/Unit . TotalItems .
   *  #ItemReceived . VendorLT . ExpiryDate . Storage . GivenTo . LT# .
   *  DistributionDate . DistributedBy . Status.
   *  Only Status is coloured (the shared status pill, §18.6) — every other cell
   *  is the app-wide ink. */
  const histCols: AppColumn<DistributionHistoryRow>[] = [
    { field: 'receiveId',         header: 'RC#',              width: 130, text: r => r.receiveId ?? '' },
    { field: 'itemName',          header: 'Item',             width: 180, text: r => r.itemName ?? '' },
    { field: 'description',       header: 'Description',      width: 230, text: r => r.description ?? '' },
    { field: 'catalog',           header: 'Catalog#',         width: 110, text: r => r.catalog ?? '' },
    { field: 'orderQty',          header: 'OrderQty',         width: 85,  type: 'number', text: r => String(r.orderQty), value: r => r.orderQty },
    { field: 'itemUnit',          header: 'Item/Unit',        width: 85,  type: 'number', text: r => String(r.itemUnit), value: r => r.itemUnit },
    { field: 'totalItems',        header: 'TotalItems',       width: 90,  type: 'number', text: r => String(r.totalItems), value: r => r.totalItems },
    { field: 'itemReceived',      header: '#ItemReceived',    width: 110, text: r => r.itemReceived ?? '' },
    { field: 'vendorLt',          header: 'VendorLT',         width: 110, text: r => r.vendorLt ?? '' },
    { field: 'expiryDate',        header: 'ExpiryDate',       width: 110, text: r => r.expiryDate ? fmtLocalDate(r.expiryDate) : '' },
    { field: 'storageName',       header: 'Storage',          width: 130, text: r => r.storageName ?? '' },
    { field: 'givenToName',       header: 'GivenTo',          width: 140, text: r => r.givenToName ?? '' },
    { field: 'lt',                header: 'LT#',              width: 110, text: r => r.lt ?? '' },
    { field: 'distributionDate',  header: 'DistributionDate', width: 130, text: r => r.distributionDate ? fmtLocalDate(r.distributionDate) : '' },
    { field: 'distributedByName', header: 'DistributedBy',    width: 140, text: r => r.distributedByName ?? '' },
    { field: 'statusLabel',       header: 'Status',           width: 130, type: 'status', text: r => r.statusLabel },
  ];

  // Detail columns — the legacy Distribution_ListView drill-down set.
  const lineCols: AppColumn<DistributionLineRow>[] = [
    { field: 'itemName',          header: 'Item',             width: 190, text: r => r.itemName ?? '—', render: r => r.itemName ?? '—' },
    { field: 'description',       header: 'Description',      width: 220, text: r => r.description ?? '', render: r => r.description ?? '' },
    { field: 'catalog',           header: 'Catalog#',         width: 110, text: r => r.catalog ?? '', render: r => r.catalog ?? '' },
    { field: 'orderQty',          header: 'OrderQty',         width: 85, type: 'number', text: r => String(r.orderQty), value: r => r.orderQty },
    { field: 'itemUnit',          header: 'Item/Unit',        width: 85, type: 'number', text: r => String(r.itemUnit), value: r => r.itemUnit },
    { field: 'totalItems',        header: 'TotalItems',       width: 90, type: 'number', text: r => String(r.totalItems), value: r => r.totalItems },
    { field: 'itemReceived',      header: '#ItemReceived',    width: 110, text: r => r.itemReceived ?? '', render: r => r.itemReceived ?? '' },
    // EDITABLE, as legacy (Distribution_ListView is InlineEditMode="Batch" and
    // leaves exactly these four without AllowEdit="False"). They are also the
    // fields the distribute rules require, so without them Save could not
    // succeed on a row that had none of them set.
    //
    // ⚠ EACH ONE MUST DECLARE `value` — that is what the shared
    // <InlineCellEditor> reads (AppDataGrid's edit branch uses
    // `col.value?.(row) ?? row[col.field]` and ignores `render`/`text`, which
    // only feed export and column filtering). Without it the editor showed the
    // UNTOUCHED row instead of the draft, so every pick vanished from the cell
    // the moment it committed and reopening the combo started blank again —
    // the "unable to save the data" report. Same pairing as the sibling
    // ReceiveItemsPage's #ItemReceiving cell.
    // The required-yet-empty tint follows the SAME three GoLive rules the Save
    // check uses (see `breaksRule`) — so what the grid marks and what the
    // message says can never disagree.
    { field: 'vendorLt',    header: 'VendorLT',   width: 110,
      // Rule 1 — only when the ITEM carries IsVendorLT.
      edit: { type: 'text', requiredEmpty: r => breaksRule(r, 1) },
      value: r => edits[r.oid]?.vendorLt ?? '',
      text:  r => edits[r.oid]?.vendorLt ?? '' },
    { field: 'expiryDate',  header: 'ExpiryDate', width: 120,
      // Rule 3 — legacy requires it; AI had not checked it at all.
      edit: { type: 'date', requiredEmpty: r => breaksRule(r, 3) },
      value: r => edits[r.oid]?.expiryDate ?? '',
      text:  r => { const v = edits[r.oid]?.expiryDate; return v ? fmtLocalDate(v) ?? '' : ''; } },
    // Rule 2 is EITHER-OR, so the pair is tinted only while BOTH are empty —
    // filling one clears the tint on both, which is what "Select givento or
    // storage." means.
    { field: 'storageName', header: 'Storage',    width: 140,
      edit: {
        type: 'combo',
        options: () => storageOpts.map(o => o.name),
        requiredEmpty: r => breaksRule(r, 2),
      },
      value: r => edits[r.oid]?.storageName ?? '',
      text:  r => edits[r.oid]?.storageName ?? '' },
    { field: 'givenToName', header: 'GivenTo',    width: 160,
      edit: {
        type: 'combo',
        options: () => employeeOpts.map(o => o.name),
        requiredEmpty: r => breaksRule(r, 2),
      },
      value: r => edits[r.oid]?.givenToName ?? '',
      text:  r => edits[r.oid]?.givenToName ?? '' },
    /* These three fill THE MOMENT A ROW IS TICKED, and clear on untick — GoLive's
       behaviour (DistributionViewController SelectionChanged ~798 stamps the date
       and the user client-side; its `distributeHandler` callback ~3179 computes
       the LT server-side and refreshes; unticking sets LT back to string.Empty).
       A stored value always wins, so an already-distributed row shows what it was
       actually given rather than a preview. */
    { field: 'lt',                header: 'LT#',              width: 110,
      text:   r => r.lt ? r.lt : ltPreviewFor(r),
      render: r => r.lt ? r.lt : ltPreviewFor(r) },
    { field: 'distributionDate',  header: 'DistributionDate', width: 130,
      text:   r => distDateFor(r),
      render: r => distDateFor(r) },
    { field: 'distributedByName', header: 'DistributedBy',    width: 150,
      text:   r => distByFor(r),
      render: r => distByFor(r) },
  ];

  /** Stage one edited cell. A combo yields the display NAME, so the matching FK
   *  is resolved HERE and both are kept — Save then posts the id it was given
   *  rather than re-matching a name at submit time.
   *
   *  Employee display names are not guaranteed unique, so a name-only combo can
   *  only ever offer the first match; that is a property of the legacy lookup,
   *  not something resolving here introduces. */
  const commitCell = useCallback((oid: string, field: string, value: string) => {
    setEdits(prev => {
      const cur = prev[oid] ?? BLANK_EDIT;
      let next: LineEdit;
      switch (field) {
        case 'vendorLt':
          next = { ...cur, vendorLt: value };
          break;
        case 'expiryDate':
          next = { ...cur, expiryDate: value };
          break;
        case 'storageName':
          next = { ...cur, storageName: value, storageId: storageOpts.find(o => o.name === value)?.id ?? null };
          break;
        case 'givenToName':
          next = { ...cur, givenToName: value, givenToId: employeeOpts.find(o => o.name === value)?.id ?? null };
          break;
        default:
          return prev;   // a display-only column — nothing to stage
      }
      return { ...prev, [oid]: next };
    });
  }, [storageOpts, employeeOpts]);


  /** The ONE message to show for a selection, or null when it is good to save.
   *
   *  RULE-MAJOR AND FIRST-FAILURE-WINS, which is the part that matters: legacy
   *  runs `lstDistributions.FirstOrDefault(<rule>)` for each rule in turn and
   *  `return`s on the first that any selected row breaks — so it scans EVERY row
   *  for rule 1 before it ever looks at rule 2, and shows exactly ONE line with
   *  no item name on it.  AI had been accumulating every broken rule for every
   *  row and concatenating them into a six-sentence wall (owner report
   *  2026-09-26); this reproduces what GoLive actually shows. */
  const firstValidationError = useCallback((rows: DistributionLineRow[]): string | null => {
    if (rows.some(r => breaksRule(r, 1))) return MSG.emptyVendorLt;
    if (rows.some(r => breaksRule(r, 2))) return MSG.selectGivenTo;
    if (rows.some(r => breaksRule(r, 3))) return MSG.expiryDate;
    return null;
  }, [breaksRule]);

  /** The History Search box filters the loaded lots client-side, the same way
   *  the batch list's does; the SCAN box is the server-side exact LT lookup. */
  const filteredHistory = useMemo(() => {
    const q = histSearch.trim().toLowerCase();
    if (!q) return histRows;
    return histRows.filter(r =>
      (r.receiveId ?? '').toLowerCase().includes(q) ||
      (r.itemName ?? '').toLowerCase().includes(q) ||
      (r.description ?? '').toLowerCase().includes(q) ||
      (r.catalog ?? '').toLowerCase().includes(q) ||
      (r.lt ?? '').toLowerCase().includes(q) ||
      (r.vendorLt ?? '').toLowerCase().includes(q) ||
      (r.storageName ?? '').toLowerCase().includes(q) ||
      (r.givenToName ?? '').toLowerCase().includes(q) ||
      (r.statusLabel ?? '').toLowerCase().includes(q));
  }, [histRows, histSearch]);

  /** Ticked rows export on their own; with nothing ticked the whole list does. */
  const historyExport = histSel.size
    ? filteredHistory.filter(r => histSel.has(r.oid))
    : filteredHistory;

  /** The detail Search box filters the loaded lines, as legacy's does. */
  const filteredLines = useMemo(() => {
    const q = lineSearch.trim().toLowerCase();
    if (!q) return lines;
    return lines.filter(r =>
      (r.itemName ?? '').toLowerCase().includes(q) ||
      (r.description ?? '').toLowerCase().includes(q) ||
      (r.catalog ?? '').toLowerCase().includes(q) ||
      (r.lt ?? '').toLowerCase().includes(q));
  }, [lines, lineSearch]);

  /** Read the server's own reason out of a failed call. The distribute /
   *  rollback SPs enforce real, named rules and the API returns them as a 400
   *  BUSINESS_RULE envelope (ExceptionHandlingMiddleware), so there is never a
   *  reason to replace a specific, actionable message with a generic one. */
  function serverMessage(err: unknown): string | undefined {
    return (err as { response?: { data?: { error?: { message?: string } } } })
      ?.response?.data?.error?.message;
  }

  /** SAVE — legacy's caption for the distribute commit on this grid. Runs the
   *  shared save flow (standing rule 7): "Do you want to Save?" → the existing
   *  audited handler → "Saved successfully." → reload the batch. */
  async function handleSave() {
    if (selected.size === 0) { notify.warning(MSG.selectRow); return; }
    const ids = Array.from(selected) as string[];
    const rows = lines.filter(r => ids.includes(r.oid));

    // Tell the user what is missing BEFORE the confirm dialog — ONE message, the
    // first rule any ticked row breaks, in GoLive's wording and GoLive's order.
    // `warning` (not `error`) because legacy raises these as
    // InformationType.Warning; the SP re-checks and would say the same thing.
    const problem = firstValidationError(rows);
    if (problem) {
      notify.warning(problem);
      return;
    }

    setActing(true);
    try {
      await runSave({
        formName: 'Item Distribution',
        auditAction: 'edit',
        save: async () => {
          await Promise.all(rows.map(r => {
            const e = edits[r.oid] ?? BLANK_EDIT;
            return api.post(`/api/v1/distributions/${r.oid}/distribute`, {
              storageId:  e.storageId,
              givenToId:  e.givenToId,
              vendorLt:   e.vendorLt.trim() || null,
              expiryDate: e.expiryDate || null,
              comment:    null,
            });
          }));
        },
        onSaved: async () => {
          setSelected(new Set());
          if (openBatch) await loadLines(openBatch.receiveId);
          await loadBatches();
        },
        onError: err => notify.error(serverMessage(err) ?? 'Save failed.'),
      });
    } finally {
      setActing(false);
    }
  }

  // ── HISTORY actions ────────────────────────────────────────────────────────
  // Legacy puts Rollback and the LT preview report on
  // Distribution_ListView_Viewmode and NOWHERE else (DistributionViewController
  // lines 68-69 set DistributeRollback.TargetViewId = "Distribution_ListView_Viewmode"
  // and LTPreviewReport.TargetViewId to the Viewmode ids). The History toggle
  // itself targets MainDistribute ONLY (line 71), which is why there is no
  // "Pending" button here — the breadcrumb is the way back, as on the drill-down.

  const histSelected = useMemo(
    () => histRows.filter(r => histSel.has(r.oid)),
    [histRows, histSel]);

  /** ROLLBACK — legacy DistributeRollback_Execute (~2679) + its
   *  RollBackReason_Accepting (~2890): refuse the batch outright if ANY selected
   *  lot is Consumed or Disposed, then capture a MANDATORY reason before
   *  touching anything, then reverse each lot to Pending Distribute. The server
   *  re-checks both rules — AI_ICM_Distribution_Rollback_SP throws 50031 / 50032
   *  — so this is the message, not the enforcement (§14b.5). */
  async function handleRollback() {
    if (histSelected.length === 0) { notify.warning(MSG.selectRow); return; }

    // Legacy aborts the WHOLE action on the first blocked row rather than
    // rolling back the rest, so a partial reversal can't happen silently.
    if (histSelected.some(r => ROLLBACK_BLOCKED.has(r.status))) {
      notify.error(MSG.rollbackFail);
      return;
    }

    if (!(await confirm({
      title: 'Rollback?',
      message: `Rollback ${histSelected.length} item(s) to Pending Distribute?`,
      tone: 'danger',
      confirmLabel: 'Rollback',
    }))) return;

    const reason = await prompt({
      title: 'Rollback',
      message: MSG.rollbackReason,
      placeholder: 'Why is this distribution being reversed?',
      required: true,
    });
    if (reason == null) return;

    setActing(true);
    try {
      await Promise.all(histSelected.map(r =>
        api.post(`/api/v1/distributions/${r.oid}/rollback`, { reason })));
      setHistSel(new Set());
      await loadHistory();
      notify.success(MSG.rollbackOk);
    } catch (err) {
      notify.error(serverMessage(err) ?? 'Rollback failed.');
    } finally {
      setActing(false);
    }
  }

  /** PREVIEW REPORT — legacy LTPreviewReport_Execute (~2569) renders the stored
   *  DevExpress "LTBarcode" layout for the SELECTED rows' Oids and hands the PDF
   *  to the download controller. AI already owns that report end to end (the
   *  QuestPDF port IcmLtLabelPdfGenerator behind
   *  GET /icm-tracking/lt-coa/label-report, shared with Item Transaction
   *  History's "Label Report" and LT# COA Tracking's "QRLabel"), so this reuses
   *  it rather than growing a second one (standing rule 8). */
  async function handlePreviewReport() {
    if (histSelected.length === 0) { notify.warning(MSG.selectRow); return; }

    // Legacy builds its id list from `.Where(i => !string.IsNullOrEmpty(i.LT))`
    // — a lot with no LT has nothing to print.
    const withLt = histSelected.filter(r => (r.lt ?? '').trim() !== '');
    if (withLt.length === 0) { notify.error(MSG.noLt); return; }

    setActing(true);
    try {
      await downloadLabelReport(withLt.map(r => r.oid));
      notify.success('Exported to PDF successfully.');
    } catch (err) {
      notify.error(serverMessage(err) ?? 'Preview report failed.');
    } finally {
      setActing(false);
    }
  }


  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>ICM Operations</span><i className="ti ti-chevron-right" />
            {openBatch ? (
              <>
                <span onClick={backToList} style={{ cursor: 'pointer' }}>Item Distribution</span>
                <i className="ti ti-chevron-right" />
                <span className="dp-bc-active">{openBatch.receiveId}</span>
              </>
            ) : history ? (
              /* History has no "Pending" button (legacy's History action targets
                 MainDistribute only), so the breadcrumb is the way back — the
                 same convention the drill-down already uses. */
              <>
                <span onClick={backToPending} style={{ cursor: 'pointer' }}>Item Distribution</span>
                <i className="ti ti-chevron-right" />
                <span className="dp-bc-active">History</span>
              </>
            ) : (
              <span className="dp-bc-active">Item Distribution</span>
            )}
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="icm-distributions" title="Item Distribution" loadMs={loadMs} loading={loading} />
          </div>
        </div>
      </div>

      {openBatch ? (
        /* ── DETAIL: the pending lines of one receive batch ── */
        <div className="bl-grid-area">
          <div style={{ margin: '0 0 8px' }}>
            {/* Legacy's Distribution_ListView bar is Save . Export to . Search
                and nothing else. AI had Back . Distribute . Rollback . Refresh .
                Export to (owner report 2026-09-25: "remove the unwanted
                controls"), so:
                  · Distribute -> SAVE. It is the same action under legacy's
                    caption -- you tick the rows and commit -- not a removal;
                  · Rollback removed. Legacy does not carry it on this bar (it
                    lives behind its own ICMRollbackViewController screen), so
                    the button goes with it. NOTE the /distributions/{id}/rollback
                    endpoint is now unreachable from the UI until that screen is
                    ported -- flagged, not silently dropped;
                  · Refresh and Back removed. Neither is on the legacy bar, and
                    the breadcrumb's "Item Distribution" already goes back;
                  · Search added, which legacy has and AI's detail did not. */}
            <AppToolbar
              left={
                <button className="ca-btn" disabled={selected.size === 0 || acting} onClick={handleSave}>
                  <BusyIcon busy={acting} icon="ti-device-floppy" /> Save
                </button>
              }
              right={
                <div className="list-search" style={{ minWidth: 240 }}>
                  <span className="tb-i">🔍</span>
                  <input type="text" value={lineSearch} onChange={e => setLineSearch(e.target.value)} placeholder="Text to search…" />
                  {lineSearch && <span className="list-search-clear" onClick={() => setLineSearch('')}>×</span>}
                </div>
              }
              exportData={{ filename: `distribution-${openBatch.receiveId}`, ...gridExportMatrix(filteredLines, lineCols) }}
            />
          </div>
          {/* NO RC#/Vendor/PO#/Receive Date header card, and the grid SIZES TO
              CONTENT on a card that fills the page: legacy's Distribution_ListView
              is the GRID ALONE, its footer directly under the rows. AI had a
              read-only summary panel above it and a grid stretched to full height,
              which left a tall empty band between the rows and the footer. Same
              anatomy and same fix as the Receiving Items detail, so both share
              `.icm-detail-card` (IcmDetailGrid.css) rather than each keeping a
              copy. RC# stays visible in the breadcrumb, and all four values are
              columns on the MASTER list you arrive from. */}
          <div className="bl-card icm-detail-card">
            <AppDataGrid<DistributionLineRow>
              rows={filteredLines}
              columns={lineCols}
              getRowId={r => r.oid}
              loading={linesLoading}
              loadMs={null}
              selectable
              selectedIds={selected}
              onSelectionChange={setSelected}
              onCellCommit={(row, col, value) => commitCell(row.oid, col.field, value)}
              emptyText="No lines pending distribution in this batch."
            />
          </div>
        </div>
      ) : history ? (
        /* ── HISTORY: one row per distributed LOT (legacy
              Distribution_ListView_Viewmode, [Status] <> PendingDistribute) ── */
        <div className="bl-grid-area">
          <div style={{ margin: '0 0 8px' }}>
            {/* Legacy's bar on THIS view: Rollback . LT Preview Report . Date
                Filter . scan box (lines 68-73). No "Pending" — the History
                action itself lives on MainDistribute only. */}
            <AppToolbar
              left={
                <>
                  <button className="ca-btn" disabled={histSel.size === 0 || acting} onClick={handleRollback}>
                    <BusyIcon busy={acting} icon="ti-arrow-back-up" /> Rollback
                  </button>
                  <button className="ca-btn" disabled={histSel.size === 0 || acting} onClick={handlePreviewReport}>
                    <BusyIcon busy={acting} icon="ti-file-type-pdf" /> Preview Report
                  </button>
                  <DateFilterSelect value={histDateFilter} onChange={setHistDateFilter} />
                  <input
                    className="sd-input"
                    style={{ width: 160, marginLeft: 8 }}
                    value={scan}
                    placeholder="Scan / LT#"
                    title="Scan or type an LT number, then press Enter — an exact match, as legacy"
                    onChange={e => setScan(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { e.preventDefault(); void loadHistory(); }
                      if (e.key === 'Escape') { setScan(''); }
                    }}
                  />
                </>
              }
              onRefresh={loadHistory}
              refreshing={loading}
              exportData={{ filename: 'distribution-history', ...gridExportMatrix(historyExport, histCols) }}
            />
          </div>
          <div className="list-search-row">
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input type="text" value={histSearch} onChange={e => setHistSearch(e.target.value)}
                placeholder="Search RC#, item, catalog#, LT#, storage…" />
              {histSearch && <span className="list-search-clear" onClick={() => setHistSearch('')}>×</span>}
            </div>
            {/* The SP caps the page at 2000 rows; say so rather than quietly
                showing a short list (the audit-trail truncation convention). */}
            {histTotal > histRows.length && (
              <span style={{ marginLeft: 12, fontSize: 12, color: 'var(--ai-muted, #6b7280)' }}>
                Showing the first {histRows.length} of {histTotal} lots — narrow the Date Filter to see the rest.
              </span>
            )}
          </div>
          <div className="bl-card bl-card-flex">
            {error ? (
              <div className="bl-err"><i className="ti ti-alert-circle" /> {error}</div>
            ) : (
              <AppDataGrid<DistributionHistoryRow>
                rows={filteredHistory}
                columns={histCols}
                getRowId={r => r.oid}
                loading={loading}
                loadMs={loadMs}
                selectable
                selectedIds={histSel}
                onSelectionChange={setHistSel}
                emptyText="No distributed lots in this window."
              />
            )}
          </div>
        </div>
      ) : (
        /* ── MASTER: one row per receive batch (RC#) ── */
        <div className="bl-grid-area">
          <div style={{ margin: '0 0 8px' }}>
            {/* HISTORY + DATE FILTER + SCAN — all three are on legacy's bar
                (Distributionview / DistributionDateFilter /
                txtBarcodeActionDistribution) and none was in AI. */}
            <AppToolbar
              left={
                <>
                  {/* One-way now: History opens its OWN view
                      (Distribution_ListView_Viewmode), which carries no
                      "Pending" button because legacy's History action targets
                      MainDistribute only. The breadcrumb there comes back. */}
                  <button className="ca-btn" onClick={() => setHistory(true)}
                    title="Show the distributed lots (Item Distribution History)">
                    <i className="ti ti-history" /> History
                  </button>
                  {/* NO Date Filter and NO Scan / LT# box on this bar (owner
                      request 2026-09-29). Both remain on the HISTORY view,
                      whose legacy bar does carry them (Rollback · LT Preview
                      Report · Date Filter · scan). The search box below the
                      toolbar still filters RC# / vendor / PO#. */}
                </>
              }
              onRefresh={loadBatches}
              refreshing={loading}
              exportData={{ filename: 'distribution-batches', ...gridExportMatrix(batchesExport, batchCols) }}
            />
          </div>
          <div className="list-search-row">
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search RC#, vendor, PO#…" />
              {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
            </div>
          </div>
          <div className="bl-card bl-card-flex">
            {error ? (
              <div className="bl-err"><i className="ti ti-alert-circle" /> {error}</div>
            ) : (
              <AppDataGrid<DistributionBatchRow>
                rows={filteredBatches}
                columns={batchCols}
                getRowId={b => b.receiveId}
                loading={loading}
                loadMs={loadMs}
                selectable
                selectedIds={batchSel}
                onSelectionChange={setBatchSel}
                onRowClick={openReceiveBatch}
                emptyText="No receive batches pending distribution."
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
